import { Fragment, useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import axios from '../../../services/api';
import { toast } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';

export default function CreateLockModal({ isOpen, onClose, onSuccess, lock = null }) {
  const { t } = useTranslation();

  // Generate 6 digit random number
  const generateUUID = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);

  };

  const [formData, setFormData] = useState({
    uuid: generateUUID().toString(), // Auto-generate 6-digit UUID for new locks
    doorId: '',
    relayId: '',
    subscriptionSubject: '',
    publishSubject: '',
    wifiName: '',
    wifiPassword: '',
  });

  useEffect(() => {
    if (lock) {
      setFormData({
        uuid: lock.uuid,
        doorId: lock.doorId,
        relayId: lock.relayId,
        subscriptionSubject: lock.subscriptionSubject,
        publishSubject: lock.publishSubject,
        wifiName: lock.wifiName || '',
        wifiPassword: lock.wifiPassword || '',
      });
    }else{
      setFormData({
        uuid: generateUUID().toString(), // Auto-generate 6-digit UUID for new locks
        doorId: '',
        relayId: '',
        subscriptionSubject: '',
        publishSubject: '',
        wifiName: '',
        wifiPassword: '',
      });
    }
  }, [lock]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.uuid.trim()) {
      toast.error(t('UUID is required'));
      return;
    }
    if (!formData.relayId.trim()) {
      toast.error(t('Relay ID is required'));
      return;
    }
    if (!formData.subscriptionSubject.trim()) {
      toast.error(t('Subscription Subject is required'));
      return;
    }
    if (!formData.publishSubject.trim()) {
      toast.error(t('Publish Subject is required'));
      return;
    }

    try {
      if (lock) {
        await axios.put(`/locks/${lock._id}`, formData);
        toast.success(t('Lock updated successfully'));
      } else {
        await axios.post('/locks', formData);
        toast.success(t('Lock created successfully'));
      }
      setFormData({
        uuid: generateUUID().toString(), // Auto-generate 6-digit UUID for new locks
        doorId: '',
        relayId: '',
        subscriptionSubject: '',
        publishSubject: '',
        wifiName: '',
        wifiPassword: '',
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[10%] pt-6 z-50">
        <div className="bg-white w-[calc(85vh*0.7)] h-[85vh] rounded-lg shadow-xl">
          {/* Black header */}
          <div className="bg-black text-white px-4 py-3 rounded-t-lg">
            <h2 className="text-lg font-semibold">
              {lock ? t('Edit Lock') : t('Create Lock')}
            </h2>
          </div>

          {/* White content area */}
          <div className="p-4 h-[calc(85vh-116px)] overflow-y-auto">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                    {t('UUID')}
                </label>
                <input
                  type="text"
                  value={formData.uuid}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2"
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                    {t('Relay ID')}
                </label>
                <input
                  type="text"
                  value={formData.relayId}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2"
                  required
                  onChange={(e) => setFormData({ ...formData, relayId: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                    {t('Subscription Subject')}*
                </label>
                <input
                  type="text"
                  value={formData.subscriptionSubject}
                  onChange={(e) => setFormData({ ...formData, subscriptionSubject: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                    {t('Publish Subject')}*
                </label>
                <input
                  type="text"
                  value={formData.publishSubject}
                  onChange={(e) => setFormData({ ...formData, publishSubject: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                    {t('Wifi Name')}
                </label>
                <input
                  type="text"
                  value={formData.wifiName}
                  onChange={(e) => setFormData({ ...formData, wifiName: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                    {t('Wifi Password')}
                </label>
                <input
                  type="text"
                  value={formData.wifiPassword}
                  onChange={(e) => setFormData({ ...formData, wifiPassword: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
          </div>

          {/* Footer with buttons */}
          <div className="px-4 py-3 border-t border-gray-200">
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                {t('Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
}